import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Bitcoin,
  BarChart3,
  Globe,
  Zap,
  Award,
  Star,
  Activity,
  Eye,
  ArrowRight
} from 'lucide-react';

// Import professional trading images
const cryptoTradingImage = require('../assets/businesswoman-bitcoin.jpg');
const stockTradingImage = require('../assets/man-is-holding-tablet-with-graph-him_479259-14595.jpg_dz4zsm.jpg');
const futuristicTradingImage = require('../assets/business-person-futuristic-business-environment.jpg');
const tradingFloorImage = require('../assets/two-stock-exchange-traders-discussing-dynamic-investment-graph-sellable_31965-313820.jpg_yeupoj.jpg');

const Markets = () => {
  const [activeMarket, setActiveMarket] = useState(0);

  // Simulate real-time price updates
  const [marketData, setMarketData] = useState({
    stocks: [
      { symbol: 'AAPL', name: 'Apple Inc.', price: 175.43, change: 2.15, trend: 'up', volume: '52.3M' },
      { symbol: 'TSLA', name: 'Tesla Inc.', price: 245.67, change: 4.32, trend: 'up', volume: '41.2M' },
      { symbol: 'MSFT', name: 'Microsoft', price: 378.85, change: -1.28, trend: 'down', volume: '28.7M' },
      { symbol: 'GOOGL', name: 'Alphabet', price: 142.56, change: 3.45, trend: 'up', volume: '35.1M' }
    ],
    crypto: [
      { symbol: 'BTC', name: 'Bitcoin', price: 43567, change: 1.28, trend: 'up', volume: '$2.1B' },
      { symbol: 'ETH', name: 'Ethereum', price: 2543, change: 3.45, trend: 'up', volume: '$1.8B' },
      { symbol: 'BNB', name: 'Binance Coin', price: 312.45, change: -2.15, trend: 'down', volume: '$423M' },
      { symbol: 'ADA', name: 'Cardano', price: 0.48, change: 5.67, trend: 'up', volume: '$387M' }
    ]
  });

  const markets = [
    {
      id: 'stocks',
      category: 'Stock Markets',
      icon: <TrendingUp className="h-8 w-8 text-emerald-600" />,
      description: 'Trade global stocks from major exchanges worldwide',
      image: stockTradingImage,
      color: 'emerald',
      marketCap: '$45T',
      instruments: '15K+',
      exchanges: '50+'
    },
    {
      id: 'crypto',
      category: 'Cryptocurrency',
      icon: <Bitcoin className="h-8 w-8 text-orange-600" />,
      description: 'Access 100+ cryptocurrencies and DeFi tokens',
      image: cryptoTradingImage,
      color: 'orange',
      marketCap: '$1.8T',
      instruments: '500+',
      exchanges: '25+'
    },
    {
      id: 'forex',
      category: 'Forex Trading',
      icon: <Globe className="h-8 w-8 text-blue-600" />,
      description: 'Major, minor, and exotic currency pairs',
      image: tradingFloorImage,
      color: 'blue',
      marketCap: '$7.5T',
      instruments: '180+',
      exchanges: '24/7'
    },
    {
      id: 'commodities',
      category: 'Commodities',
      icon: <BarChart3 className="h-8 w-8 text-yellow-600" />,
      description: 'Gold, silver, oil, and agricultural commodities',
      image: futuristicTradingImage,
      color: 'yellow',
      marketCap: '$20T',
      instruments: '100+',
      exchanges: '30+'
    }
  ];

  const marketHighlights = [
    { label: 'Total Markets', value: '150+', icon: <Globe className="h-5 w-5" /> },
    { label: 'Daily Volume', value: '$2.5T', icon: <Activity className="h-5 w-5" /> },
    { label: 'Live Feeds', value: '24/7', icon: <Zap className="h-5 w-5" /> },
    { label: 'Instruments', value: '50K+', icon: <BarChart3 className="h-5 w-5" /> }
  ];

  // Simulate price movements
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketData(prev => ({
        stocks: prev.stocks.map(stock => ({
          ...stock,
          price: stock.price + (Math.random() - 0.5) * 2,
          change: stock.change + (Math.random() - 0.5) * 0.5
        })),
        crypto: prev.crypto.map(crypto => ({
          ...crypto,
          price: crypto.price + (Math.random() - 0.5) * (crypto.price * 0.02),
          change: crypto.change + (Math.random() - 0.5) * 0.8
        }))
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="markets-section">
      <div className="markets-container">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="markets-header"
        >
          <div className="markets-badge">
            <Award className="markets-badge-icon" />
            <span>Global Markets Access</span>
          </div>
          <h2 className="markets-title">
            Trade
            <span className="markets-title-gradient"> Global Markets</span>
            <br />
            From One Platform
          </h2>
          <p className="markets-subtitle">
            Access stocks, cryptocurrencies, forex, and commodities from major exchanges worldwide. 
            Experience professional trading with competitive spreads and lightning-fast execution.
          </p>
        </motion.div>

        {/* Market Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {marketHighlights.map((highlight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              className="bg-white/10 backdrop-blur-sm border border-emerald-500/30 rounded-xl p-6 text-center"
            >
              <div className="flex items-center justify-center mb-3 text-emerald-400">
                {highlight.icon}
              </div>
              <div className="text-2xl font-bold text-white mb-1">{highlight.value}</div>
              <div className="text-sm text-slate-300">{highlight.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Interactive Markets Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          
          {/* Market Categories Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-4"
          >
            {markets.map((market, index) => (
              <motion.button
                key={market.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveMarket(index)}
                className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${
                  activeMarket === index 
                    ? 'bg-white/20 border-2 border-emerald-500/50' 
                    : 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50'
                }`}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    market.color === 'emerald' ? 'bg-emerald-500/20' :
                    market.color === 'orange' ? 'bg-orange-500/20' :
                    market.color === 'blue' ? 'bg-blue-500/20' :
                    'bg-yellow-500/20'
                  }`}>
                    {market.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{market.category}</h3>
                    <p className="text-sm text-slate-400">{market.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-white font-semibold">{market.marketCap}</div>
                    <div className="text-xs text-slate-400">Market Cap</div>
                  </div>
                  <div>
                    <div className="text-white font-semibold">{market.instruments}</div>
                    <div className="text-xs text-slate-400">Instruments</div>
                  </div>
                  <div>
                    <div className="text-white font-semibold">{market.exchanges}</div>
                    <div className="text-xs text-slate-400">Exchanges</div>
                  </div>
                </div>
              </motion.button>
            ))}
          </motion.div>

          {/* Market Display Area */}
          <motion.div
            key={activeMarket}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
              
              {/* Market Header with Image */}
              <div className="relative h-48">
                <img
                  src={markets[activeMarket].image}
                  alt={markets[activeMarket].category}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
                <div className="absolute bottom-4 left-6 right-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">
                        {markets[activeMarket].category}
                      </h3>
                      <p className="text-slate-300">{markets[activeMarket].description}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                      {markets[activeMarket].icon}
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Data Feed */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-lg font-bold text-white">Live Market Data</h4>
                  <div className="flex items-center gap-2 text-emerald-400">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                    <span className="text-sm">LIVE</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {(activeMarket === 0 ? marketData.stocks : marketData.crypto).map((asset, index) => (
                    <motion.div
                      key={asset.symbol}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl border border-slate-600/30 hover:bg-slate-700/50 transition-all duration-300"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {asset.symbol.slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <div className="font-bold text-white">{asset.symbol}</div>
                          <div className="text-sm text-slate-400">{asset.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-white">
                          ${asset.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className={`flex items-center gap-1 text-sm ${
                          asset.change >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {asset.change >= 0 ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          )}
                          {asset.change >= 0 ? '+' : ''}{asset.change.toFixed(2)}%
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Trade Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full mt-6 py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    markets[activeMarket].color === 'emerald' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30' :
                    markets[activeMarket].color === 'orange' ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30' :
                    markets[activeMarket].color === 'blue' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30' :
                    'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/30'
                  }`}
                >
                  Start Trading {markets[activeMarket].category}
                  <ArrowRight className="h-4 w-4" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Zap className="h-8 w-8 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">Lightning Fast</h3>
            <p className="text-slate-300">Execute trades in milliseconds with our advanced technology</p>
          </div>

          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Globe className="h-8 w-8 text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">Global Access</h3>
            <p className="text-slate-300">Trade markets from around the world on a single platform</p>
          </div>

          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Star className="h-8 w-8 text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">Best Prices</h3>
            <p className="text-slate-300">Competitive spreads and zero commission on major instruments</p>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="markets-cta"
        >
          <div className="markets-cta-content">
            <h3 className="markets-cta-title">
              Ready to Trade Global Markets?
            </h3>
            <p className="markets-cta-subtitle">
              Join millions of traders accessing markets worldwide with competitive pricing and professional tools
            </p>
            <div className="markets-cta-buttons">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(34, 197, 94, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="markets-cta-primary"
              >
                Open Trading Account
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="markets-cta-secondary"
              >
                Explore All Markets
              </motion.button>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Markets; 